/**
 * QueueStatusSection - Current queue status for the user's barber
 */

import { useTranslation } from 'react-i18next';
import type { QueueTicket } from '../../../types';
import { getStatusBadge, getStatusText } from './statusHelpers';

interface QueueStatusSectionProps {
  myTicket: QueueTicket;
  queueTickets: QueueTicket[];
  currentUserId?: string;
}

export function QueueStatusSection({ myTicket, queueTickets, currentUserId }: QueueStatusSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-surface border border-hairline rounded-xl p-6">
      <h2 className="font-sans text-lg font-semibold text-ink mb-4">
        Estado de la Cola{myTicket.barberName ? ` — ${myTicket.barberName}` : ''}
      </h2>
      {queueTickets.length === 0 ? (
        <p className="text-sm text-ink-muted">No hay personas en cola</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ink-muted mb-3">
            {queueTickets.length} {queueTickets.length === 1 ? 'persona' : 'personas'} en cola
          </p>
          {queueTickets.slice(0, 5).map((ticket) => (
            <div
              key={ticket.queueId}
              className={`flex justify-between items-center p-3 rounded-lg border ${
                ticket.userId === currentUserId ? 'border-accent/50 bg-accent/5' : 'border-hairline bg-surface-2'
              }`}
            >
              <div>
                <p className="font-semibold text-ink">
                  {ticket.ticketNumber}
                  {ticket.userId === currentUserId && (
                    <span className="ml-2 text-accent text-sm">(Tú)</span>
                  )}
                </p>
                <p className="text-sm text-ink-muted">
                  {(ticket.status === 'in_service' || ticket.status === 'finishing')
                    ? getStatusText(ticket.status, t)
                    : `Posición ${ticket.position} · ${getStatusText(ticket.status, t)}`}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(ticket.status)}`}>
                {ticket.status}
              </span>
            </div>
          ))}
          {queueTickets.length > 5 && (
            <p className="text-sm text-ink-subtle text-center mt-3">
              … y {queueTickets.length - 5} más
            </p>
          )}
        </div>
      )}
    </div>
  );
}
